'use client';

import { cn } from '@/utils/styles';

import { AddCircle, RemoveCircle } from '@1debit/icons';
import { liveUpdateAttrs } from '@/libs/contentful/utils';

import CTA from '@/components/shared/CTA/CTA';
import Media from '@/components/shared/Media/Media';
import RichTextCopy from '@/components/shared/RichTextCopy/RichTextCopy';
import SplitRichTextHeadline from '../SplitRichTextHeadline';
import { CardsVerticalScrollItemCovariantProps } from '../types';
import { getRichTextValue } from '@/utils/formatters';
import { getBulletListStyle } from '@/utils/bulletListHelpers';

const MobileCardsVerticalScrollItem = ({
  sys,
  headline,
  copy,
  desktopMedia,
  mobileMedia,
  cta,
  bulletStyle,
  isExpanded,
  onExpand,
}: CardsVerticalScrollItemCovariantProps) => {
  const plainHeadline = getRichTextValue(headline);

  const internalClassNames = {
    wrapper: cn('sm:hidden flex flex-col gap-4 items-center pt-6 px-5', {
      'pt-10 pb-26': isExpanded,
    }),

    header: {
      wrapper: cn('w-full flex justify-between items-start gap-4'),
      collapseButton: cn('flex items-center rounded-full cursor-pointer'),
    },

    headline: {
      wrapper: cn('font-sans font-semibold text-heading-sm leading-[1.25] tracking-[-0.036em]'),
      preamble: cn('text-heading-sm'),
      callout: cn('text-heading-lg'),
    },

    innerContent: {
      wrapper: cn('flex flex-col gap-4'),
      copy: cn('font-serif text-body-serif text-md'),
      cta: cn('max-w-2/3 sm:max-w-1/2'),
    },

    media: cn('mt-11', { hidden: !isExpanded }),
  };

  const CardHeadline = () => (
    <div className={internalClassNames.header.wrapper}>
      <SplitRichTextHeadline headline={headline} isExpanded={isExpanded} />
      <button
        data-testid="mobile-vertical-card-control"
        className={internalClassNames.header.collapseButton}
        aria-controls={`${sys?.id}-content`}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${plainHeadline} Card`}
        onClick={onExpand}>
        {isExpanded ? <RemoveCircle className="w-8 h-8" /> : <AddCircle className="w-8 h-8" />}
      </button>
    </div>
  );

  const CardContent = () => (
    <div className={internalClassNames.innerContent.wrapper}>
      <div
        className={cn({ hidden: !isExpanded }, internalClassNames.innerContent.copy)}
        {...liveUpdateAttrs(sys?.id, 'copy')}
        data-testid="card-expanded-copy">
        <RichTextCopy copy={copy} bulletListStyle={getBulletListStyle(bulletStyle)} />
      </div>
      {cta && (
        <CTA
          cta={cta}
          variant="outline"
          className={cn({ hidden: !isExpanded }, internalClassNames.innerContent.cta)}
          testId="card-expanded-cta"
        />
      )}
    </div>
  );

  return (
    <div className={internalClassNames.wrapper}>
      <CardHeadline />
      <CardContent />
      {desktopMedia && (
        <Media
          media={desktopMedia}
          mobileMedia={mobileMedia}
          priority={true}
          sizes="(max-width: 751px) 100vw, (max-width: 1440px) 50vw, 760px"
          className={internalClassNames.media}
        />
      )}
    </div>
  );
};

export default MobileCardsVerticalScrollItem;
